import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useTimeline } from '../../hooks/useTimeline';
import {
  PostCategory,
  PostStatus,
  PostCategoryLabels,
  PostStatusLabels,
  TimelinePostDetail,
  CreateTimelinePostRequest,
  UpdateTimelinePostRequest,
} from '../../types/timeline';
import { CompanyLayout } from '../../components/Layout';
import { formatRelativeTime } from '../../utils/dateUtils';
import { TimelineImageUploader } from '../../components/Timeline/TimelineImageUploader';
import { useCompanyId } from '../../hooks/useCompanyId';
import { LinkifiedText } from '../../components/Common/LinkifiedText';

const TimelineManagementPage: React.FC = () => {
  const {
    posts,
    loading,
    postsLoading,
    createLoading,
    pagination,
    companyStats,
    loadPosts,
    createPost,
    updatePost,
    deletePost,
    loadCompanyStats,
    filterPosts,
    loadPage,
  } = useTimeline();

  const [categoryFilter, setCategoryFilter] = useState<PostCategory | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPost, setEditingPost] = useState<TimelinePostDetail | null>(
    null
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingPost, setDeletingPost] = useState<TimelinePostDetail | null>(
    null
  );

  // 企業IDを取得
  const { companyId, loading: companyIdLoading } = useCompanyId();

  // 企業IDとフィルター変更時のデータ読み込み（統合版）
  useEffect(() => {
    let mounted = true;

    if (companyId && !companyIdLoading) {
      // 企業固有の投稿を読み込む（フィルター適用）
      loadPosts({
        company_id: companyId,
        category: categoryFilter || undefined,
        search: searchQuery || undefined,
      }).catch((err) => {
        if (mounted) {
          console.error('投稿データ読み込みエラー:', err);
        }
      });
    }

    return () => {
      mounted = false;
    };
  }, [companyId, companyIdLoading, categoryFilter, searchQuery, loadPosts]);

  // 企業統計データの読み込み（企業IDが変わった時のみ）
  useEffect(() => {
    let mounted = true;

    if (companyId && !companyIdLoading) {
      loadCompanyStats(companyId).catch((err) => {
        if (mounted) {
          console.error('企業統計データ読み込みエラー:', err);
        }
      });
    }

    return () => {
      mounted = false;
    };
  }, [companyId, companyIdLoading, loadCompanyStats]);

  // フィルターリセット
  const handleResetFilters = () => {
    setCategoryFilter('');
    setSearchQuery('');
    // フィルター変更のuseEffectが自動的に再取得する
  };

  // ページ変更
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (companyId) {
      loadPage(page, {
        company_id: companyId,
        category: categoryFilter || undefined,
        search: searchQuery || undefined,
      });
    }
  };

  // 投稿作成
  const handleCreatePost = () => {
    setShowCreateDialog(true);
  };

  // 投稿編集
  const handleEditPost = (post: TimelinePostDetail) => {
    setEditingPost(post);
    setShowEditDialog(true);
  };

  // 投稿削除
  const handleDeletePost = (post: TimelinePostDetail) => {
    setDeletingPost(post);
    setShowDeleteDialog(true);
  };

  // 削除確認
  const confirmDelete = async () => {
    if (deletingPost && companyId) {
      try {
        await deletePost(deletingPost.id);
        
        // 削除後のページネーション処理
        // 現在のページに投稿が1つしかなく、最後のページでない場合は前のページに戻る
        const isLastItemOnPage = posts.length === 1;
        const isNotFirstPage = pagination.current_page > 1;
        
        if (isLastItemOnPage && isNotFirstPage) {
          // 前のページに戻る
          await loadPage(pagination.current_page - 1, {
            company_id: companyId,
            category: categoryFilter || undefined,
            search: searchQuery || undefined,
          });
        } else {
          // 現在のページを再読み込み
          await loadPosts({
            company_id: companyId,
            category: categoryFilter || undefined,
            search: searchQuery || undefined,
            page: pagination.current_page,
          });
        }
        
        setShowDeleteDialog(false);
        setDeletingPost(null);
      } catch (error) {
        console.error('投稿削除エラー:', error);
      }
    }
  };

  // ステータス別の色設定
  const getStatusColor = (status: PostStatus) => {
    switch (status) {
      case PostStatus.PUBLISHED:
        return 'success';
      case PostStatus.DRAFT:
        return 'warning';
      case PostStatus.EXPIRED:
        return 'error';
      default:
        return 'default';
    }
  };

  // カテゴリ別の色設定
  const getCategoryColor = (category: PostCategory) => {
    switch (category) {
      case PostCategory.RECRUITMENT:
        return 'primary';
      case PostCategory.EVENT:
        return 'secondary';
      case PostCategory.COMPANY_NEWS:
        return 'info';
      case PostCategory.OTHER:
        return 'default';
      default:
        return 'default';
    }
  };

  // 企業ID読み込み中
  if (companyIdLoading) {
    return (
      <CompanyLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      </CompanyLayout>
    );
  }

  // 企業IDが取得できない場合
  if (!companyId) {
    return (
      <CompanyLayout>
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
          <Typography variant="h6" color="error">
            企業情報の取得に失敗しました
          </Typography>
        </Box>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* ヘッダー */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              タイムライン管理
            </Typography>
            <Typography variant="body1" color="text.secondary">
              学生向けの投稿を作成・管理できます
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePost}
            size="large"
          >
            新規投稿
          </Button>
        </Box>

        {/* 統計情報 */}
        {companyStats && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              投稿統計
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {companyStats.total_posts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    総投稿数
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {companyStats.total_views.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    総閲覧数
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="error.main">
                    {companyStats.total_likes.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    総いいね数
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {companyStats.published_posts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    公開中
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* フィルター・検索エリア */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            フィルター・検索
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="投稿内容を検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>カテゴリ</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) =>
                    setCategoryFilter(e.target.value as PostCategory | '')
                  }
                  label="カテゴリ"
                >
                  <MenuItem value="">すべて</MenuItem>
                  {Object.entries(PostCategoryLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                disabled={postsLoading}
                fullWidth
              >
                リセット
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* 投稿一覧 */}
        {postsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : posts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              投稿が見つかりませんでした
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              新しい投稿を作成するか、検索条件を変更してください
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreatePost}
              sx={{ mt: 2 }}
            >
              新規投稿を作成
            </Button>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {posts.map((post) => (
              <CompanyPostCard
                key={post.id}
                post={post}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
                getStatusColor={getStatusColor}
                getCategoryColor={getCategoryColor}
              />
            ))}
          </Stack>
        )}

        {/* ページネーション */}
        {pagination.total_pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={pagination.total_pages}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}

        {/* 投稿作成ダイアログ */}
        <PostCreateDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onCreate={async (data) => {
            await createPost(data);
            // 投稿作成後に企業の投稿一覧を再読み込み
            if (companyId) {
              await loadPosts({ company_id: companyId });
            }
          }}
          loading={createLoading}
        />

        {/* 投稿編集ダイアログ */}
        <PostEditDialog
          open={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setEditingPost(null);
          }}
          onUpdate={updatePost}
          post={editingPost}
          loading={loading}
        />

        {/* 削除確認ダイアログ */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
        >
          <DialogTitle>投稿を削除しますか？</DialogTitle>
          <DialogContent>
            <Typography>
              この操作は取り消せません。本当に削除しますか？
            </Typography>
            {deletingPost && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {deletingPost.title || '（タイトルなし）'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {deletingPost.content.substring(0, 100)}...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>
              キャンセル
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={loading}
            >
              削除
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </CompanyLayout>
  );
};

// 企業投稿カードコンポーネント
interface CompanyPostCardProps {
  post: TimelinePostDetail;
  onEdit: (post: TimelinePostDetail) => void;
  onDelete: (post: TimelinePostDetail) => void;
  getStatusColor: (
    status: PostStatus
  ) => 'success' | 'warning' | 'error' | 'default';
  getCategoryColor: (
    category: PostCategory
  ) => 'primary' | 'secondary' | 'info' | 'default';
}

const CompanyPostCard: React.FC<CompanyPostCardProps> = ({
  post,
  onEdit,
  onDelete,
  getStatusColor,
  getCategoryColor,
}) => {
  return (
    <Card>
      <CardContent>
        {/* ステータス・カテゴリ */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Stack direction="row" spacing={1}>
            <Chip
              label={PostStatusLabels[post.status]}
              color={getStatusColor(post.status)}
              size="small"
            />
            <Chip
              label={PostCategoryLabels[post.category]}
              color={getCategoryColor(post.category)}
              size="small"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {formatRelativeTime(post.created_at)}
          </Typography>
        </Box>

        {/* 投稿タイトル */}
        {post.title && (
          <Typography variant="h6" component="h2" gutterBottom>
            {post.title}
          </Typography>
        )}

        {/* 投稿内容（プレビュー） */}
        <LinkifiedText
          text={
            post.content.length > 200
              ? `${post.content.substring(0, 200)}...`
              : post.content
          }
          variant="body1"
          sx={{ mb: 2 }}
        />

        {/* 投稿画像 */}
        {post.image_url && (
          <Box sx={{ mb: 2 }}>
            <img
              src={post.image_url}
              alt="投稿画像"
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* エンゲージメント統計 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VisibilityIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {post.view_count.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FavoriteIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {post.like_count.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions>
        <Button
          startIcon={<EditIcon />}
          onClick={() => onEdit(post)}
          size="small"
        >
          編集
        </Button>
        <Button
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(post)}
          color="error"
          size="small"
        >
          削除
        </Button>
      </CardActions>
    </Card>
  );
};

// 投稿作成ダイアログ（簡易版）
interface PostCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: CreateTimelinePostRequest) => Promise<any>;
  loading: boolean;
}

const PostCreateDialog: React.FC<PostCreateDialogProps> = ({
  open,
  onClose,
  onCreate,
  loading,
}) => {
  const [formData, setFormData] = useState<CreateTimelinePostRequest>({
    title: '',
    content: '',
    category: PostCategory.OTHER,
    image_url: undefined,
  });

  // ダイアログが閉じられた時にフォームをリセット
  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      category: PostCategory.OTHER,
      image_url: undefined,
    });
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await onCreate(formData);
      handleClose(); // 成功時はフォームをリセットして閉じる

      // 投稿一覧を再読み込み（親コンポーネントで実行される）
    } catch (error) {
      // エラーハンドリングはuseTimelineで行う
      console.error('投稿作成エラー:', error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>新規投稿作成</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="タイトル（任意）"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            fullWidth
            error={Boolean(
              formData.title &&
                formData.title.trim().length > 0 &&
                formData.title.trim().length < 5
            )}
            helperText={
              formData.title &&
              formData.title.trim().length > 0 &&
              formData.title.trim().length < 5
                ? 'タイトルは5文字以上で入力してください'
                : formData.title
                  ? `${formData.title.length}/100文字`
                  : '任意項目'
            }
          />
          <FormControl fullWidth>
            <InputLabel>カテゴリ</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  category: e.target.value as PostCategory,
                })
              }
              label="カテゴリ"
            >
              {Object.entries(PostCategoryLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="投稿内容"
            value={formData.content}
            onChange={(e) =>
              setFormData({ ...formData, content: e.target.value })
            }
            multiline
            rows={6}
            fullWidth
            required
            error={Boolean(
              formData.content.trim().length > 0 &&
                formData.content.trim().length < 10
            )}
            helperText={
              formData.content.trim().length > 0 &&
              formData.content.trim().length < 10
                ? '投稿内容は10文字以上で入力してください'
                : `${formData.content.length}/2000文字`
            }
          />
          <TimelineImageUploader
            value={formData.image_url}
            onChange={(imageUrl) =>
              setFormData({ ...formData, image_url: imageUrl })
            }
            disabled={loading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>キャンセル</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData.content.trim() ||
            formData.content.trim().length < 10 ||
            (formData.title &&
              formData.title.trim().length > 0 &&
              formData.title.trim().length < 5)
          }
        >
          投稿する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 投稿編集ダイアログ（簡易版）
interface PostEditDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (postId: number, data: UpdateTimelinePostRequest) => Promise<any>;
  post: TimelinePostDetail | null;
  loading: boolean;
}

const PostEditDialog: React.FC<PostEditDialogProps> = ({
  open,
  onClose,
  onUpdate,
  post,
  loading,
}) => {
  const [formData, setFormData] = useState<UpdateTimelinePostRequest>({});

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title || '',
        content: post.content,
        category: post.category,
        status: post.status,
        image_url: post.image_url,
      });
    }
  }, [post]);

  const handleSubmit = async () => {
    if (!post) return;

    try {
      await onUpdate(post.id, formData);
      onClose();
    } catch (error) {
      // エラーハンドリングはuseTimelineで行う
    }
  };

  if (!post) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>投稿編集</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="タイトル（任意）"
            value={formData.title || ''}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            fullWidth
            error={Boolean(
              formData.title &&
                formData.title.trim().length > 0 &&
                formData.title.trim().length < 5
            )}
            helperText={
              formData.title &&
              formData.title.trim().length > 0 &&
              formData.title.trim().length < 5
                ? 'タイトルは5文字以上で入力してください'
                : formData.title
                  ? `${formData.title.length}/100文字`
                  : '任意項目'
            }
          />
          <FormControl fullWidth>
            <InputLabel>カテゴリ</InputLabel>
            <Select
              value={formData.category || post.category}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  category: e.target.value as PostCategory,
                })
              }
              label="カテゴリ"
            >
              {Object.entries(PostCategoryLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={formData.status || post.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as PostStatus,
                })
              }
              label="ステータス"
            >
              {Object.entries(PostStatusLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="投稿内容"
            value={formData.content || post.content}
            onChange={(e) =>
              setFormData({ ...formData, content: e.target.value })
            }
            multiline
            rows={6}
            fullWidth
            required
            error={Boolean(
              (formData.content || post.content).trim().length > 0 &&
                (formData.content || post.content).trim().length < 10
            )}
            helperText={
              (formData.content || post.content).trim().length > 0 &&
              (formData.content || post.content).trim().length < 10
                ? '投稿内容は10文字以上で入力してください'
                : `${(formData.content || post.content).length}/2000文字`
            }
          />
          <TimelineImageUploader
            value={formData.image_url}
            onChange={(imageUrl) =>
              setFormData({ ...formData, image_url: imageUrl })
            }
            disabled={loading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !(formData.content || post.content).trim() ||
            (formData.content || post.content).trim().length < 10 ||
            (formData.title &&
              formData.title.trim().length > 0 &&
              formData.title.trim().length < 5)
          }
        >
          更新する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimelineManagementPage;
